import React, { memo, useLayoutEffect, useRef, useState } from 'react';

/**
 * CollapsedSlot - Wrapper for collapsed column slots in swimlane mode
 * Uses FLIP animation (like SwimlaneColumnCell) to stay in sync with column reordering.
 */
const CollapsedSlot = memo(({ 
  columnId, 
  columnIndex,
  isCollapsed, 
  columnCount, 
  column, 
  onToggleCollapse, 
  activeColumnId,
}) => {
  const isBeingDragged = activeColumnId === columnId;

  // FLIP animation: track previous index to calculate animation offset
  const prevIndexRef = useRef(columnIndex);
  const [animationOffset, setAnimationOffset] = useState(0);

  useLayoutEffect(() => {
    if (prevIndexRef.current !== columnIndex && activeColumnId) {
      const indexDelta = prevIndexRef.current - columnIndex;
      // Collapsed columns: 40px + 16px margin = 56px
      // Expanded columns: 320px + 16px margin = 336px
      // Use collapsed width since this component is for collapsed columns
      const columnWidth = isCollapsed ? 56 : 336;
      const offset = indexDelta * columnWidth;

      // Invert
      setAnimationOffset(offset);

      // Play
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationOffset(0);
        });
      });
    }

    prevIndexRef.current = columnIndex;
  }, [columnIndex, activeColumnId, isCollapsed]);

  const style = {
    zIndex: isBeingDragged ? 1000 : 'auto',
    transform: animationOffset !== 0 ? `translateX(${animationOffset}px)` : undefined,
    transition: animationOffset === 0 && activeColumnId ? 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
  };

  return (
    <div 
      style={style}
      className={`kanban-swimlanes__collapsed-slot ${isCollapsed ? 'kanban-swimlanes__collapsed-slot--active' : ''}`}
    >
      {isCollapsed && (
        <div 
          className={`kanban-swimlanes__collapsed-strip ${isBeingDragged ? 'kanban-swimlanes__collapsed-strip--dragging' : ''}`}
          onClick={isBeingDragged ? undefined : (e) => { e.stopPropagation(); onToggleCollapse(columnId); }}
          title={isBeingDragged ? undefined : `Expand ${column.name}`}
        >
          {/* Dead zone for drag handle area - don't trigger expand when clicking here */}
          <div 
            className="kanban-swimlanes__collapsed-strip-header-zone"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="kanban-swimlanes__collapsed-drag-handle">
              <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                <circle cx="2" cy="2" r="1.5" />
                <circle cx="8" cy="2" r="1.5" />
                <circle cx="2" cy="7" r="1.5" />
                <circle cx="8" cy="7" r="1.5" />
                <circle cx="2" cy="12" r="1.5" />
                <circle cx="8" cy="12" r="1.5" />
              </svg>
            </span>
          </div>
          <span 
            className="kanban-swimlanes__header-color"
            style={{ backgroundColor: column.color }}
          />
          <span className="kanban-swimlanes__header-label">{column.name}</span>
          <span className="kanban-swimlanes__header-count">{columnCount}</span>
        </div>
      )}
    </div>
  );
});

export default CollapsedSlot;

